/**
 * The WindowEventStream sends a stream of events related to windows to the subscriber. Since this is privileged information
 * only a single connection is allowed (from Finsemble's Window Service).
 *
 * This module is used by the window service to keep track of all changes to window state, keep its local redux store up to date,
 * and to react to those changes when appropriate.
 *
 * This code is unrelated to the code that the window service uses to effect magnetic snap & dock by listening to will-move and will-resize events.
 *
 * Note that an addition event is sent in MainWindow.setBounds() to provide a stream of intermediate location changes while the
 * end user is moving the window.
 */
import { getOrCreateMainLogger } from "../logger/main";
import { screen, ipcMain, IpcMainEvent, WebContents, BrowserWindow } from "electron";
import { windowsIndex } from "./mainStore";
import { MainWindow } from "./MainWindow";
import { PhysicalWindowStateEvent, WindowState } from "../types/types";

const logger = getOrCreateMainLogger();

/**
 * The webContents of the window service. The WindowEventStream is sent to this webContents. This will be null
 * until the window service subscribes.
 */
export let finsembleWindowService: WebContents | null = null;

/**
 * These are the events that the window service needs to know about.
 */
const eventsToFollow = [
	"always-on-top-changed",
	"blur",
	"closed",
	"focus",
	"hide",
	"maximize",
	"minimize",
	"move",
	"resize",
	"resized",
	"restore",
	"show",
	"unmaximize",
];

/**
 * Adds a WebContents to the WindowEventStream. This is called for BrowserWindows and BrowserViews.
 * @param webContents
 * @param mainWindow
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const addWebContentsToWindowStream = (webContents: WebContents, mainWindow: MainWindow) => {};

const getWindowState = (browserWindow: BrowserWindow): WindowState => {
	if (browserWindow.isMinimized()) return "minimized";
	if (browserWindow.isMaximized()) return "maximized";
	if (browserWindow.isFullScreen()) return "full-screen";
	if (browserWindow.isKiosk()) return "kiosk";
	return "normal";
};

/**
 * Returns the dimensions of a BrowserWindow, taking into account that on MS Windows, a maximized
 * window is 7 pixels too large in every dimension so instead we return the dimensions of the monitor's
 * workArea, which by definition is the actual size of a maximized window.
 */
const getDimensions = (browserWindow: BrowserWindow) => {
	const bounds = browserWindow.getBounds();
	return browserWindow.isMaximized() ? screen.getDisplayMatching(bounds).workArea : bounds;
};

/**
 * Adds a window to the WindowEventStream. Each of the events that the window service requires will be listened for on the associated
 * BrowserWindow. These events will be relayed up the the window service.
 *
 * Each event that is passed to the window service will include the window name, the type of event and the current window bounds.
 * title and isAlwaysOnTop will be null unless those fields have updated.
 *
 * Note, the event listeners that are added to the BrowserView do not need to be explicitly removed because the BrowserWindow itself
 * is deleted when a window closes and thus the event handlers are automatically cleaned up by Electron.
 *
 * Note: We don't listen for a "moved" event because it just an alias for the "move" event and only triggers on Mac
 *
 * @param mainWindow A MainWindow
 */
export const addWindowToWindowStream = (mainWindow: MainWindow) => {
	const { windowName } = mainWindow;
	const browserWindow = mainWindow.win;
	eventsToFollow.forEach((eventName) => {
		const handler = () => {
			if (eventName === "closed") {
				finsembleWindowService?.send("e2o.WindowEventStream", {
					eventName: "closed",
					windowName,
					open: false,
				});
			} else {
				finsembleWindowService?.send("e2o.WindowEventStream", {
					eventName,
					windowName,
					open: true,
					normalBounds: browserWindow.getNormalBounds(),
					bounds: getDimensions(browserWindow),
					isAlwaysOnTop: browserWindow.isAlwaysOnTop(),
					visible: browserWindow.isVisible(),
					windowState: getWindowState(browserWindow),
					focused: browserWindow.isFocused(),
				} as PhysicalWindowStateEvent);
			}
		};
		browserWindow.on(eventName as any, handler);
	});
	addWebContentsToWindowStream(browserWindow.webContents, mainWindow);
	finsembleWindowService?.send("e2o.WindowEventStream", {
		eventName: "opened",
		windowName,
		open: true,
		bounds: browserWindow.getBounds(),
		isAlwaysOnTop: browserWindow.isAlwaysOnTop(),
		visible: browserWindow.isVisible(),
		windowState: getWindowState(browserWindow),
		focused: browserWindow.isFocused(),
	} as PhysicalWindowStateEvent);
};

/**
 * Waits for the window service to subscribe to the WindowEventStream. Only one subscriber is allowed (to prevent eavesdropping).
 */
export const listenForWindowStreamSubscriber = () => {
	ipcMain.on("e2o.SubscribeToWindowEventStream", (event: IpcMainEvent) => {
		const webContentsId = event.sender.id;
		const entry = windowsIndex.byWebContentsId[webContentsId];
		if (finsembleWindowService) {
			logger.error(
				`Multiple subscription attempts to e2o.WindowEventStreamSubscribe. Ignoring second attempt from ${entry?.webContentsName}`
			);
			return;
		}
		logger.debug(`Establishing e2o.WindowEventStreamSubscribe connection to ${entry?.webContentsName}`);
		finsembleWindowService = event.sender;
	});
};
